// src/belediye/redux/slices/internalTasksSlice.js
// FAZ-28 — Kurum İçi Görevler durumu.
//
// Sade tutuldu: iyimser güncelleme YOK — aksiyon sonrası detay/listeler
// yeniden çekilir (v1). Sayaçlar sidebar rozetleri + sayfa şeritleri içindir.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import * as apiSvc from "../../services/internalTasksApi";

// ── Thunks ─────────────────────────────────────────────────────────────
export const fetchTasksThunk = createAsyncThunk(
  "internalTasks/fetchList",
  async ({ listKey, params }, { rejectWithValue }) => {
    try {
      const data = await apiSvc.fetchTasks(params);
      return { listKey, data };
    } catch (e) {
      return rejectWithValue({ listKey, error: e?.response?.data || "Liste alınamadı" });
    }
  }
);

// FAZ-57: arg artık {scope, params} olabilir — sayaçlar listeyle aynı arama/
// filtre kümesini sayar. Düz string arg (eski çağıranlar) da çalışır.
export const fetchCountersThunk = createAsyncThunk(
  "internalTasks/fetchCounters",
  async (arg, { rejectWithValue }) => {
    const scope = typeof arg === "string" ? arg : arg?.scope;
    const params = typeof arg === "string" ? {} : arg?.params || {};
    try {
      return { scope, data: await apiSvc.fetchCounters(scope, params) };
    } catch (e) {
      return rejectWithValue(e?.response?.data || "Sayaçlar alınamadı");
    }
  }
);

export const fetchTaskDetailThunk = createAsyncThunk(
  "internalTasks/fetchDetail",
  async (id, { rejectWithValue }) => {
    try {
      return await apiSvc.fetchTaskDetail(id);
    } catch (e) {
      return rejectWithValue(
        e?.response?.status === 404 ? "Talep bulunamadı." : "Talep yüklenemedi."
      );
    }
  }
);

export const fetchRecentTargetsThunk = createAsyncThunk(
  "internalTasks/fetchRecents",
  async (_, { rejectWithValue }) => {
    try {
      return await apiSvc.fetchRecentTargets();
    } catch (e) {
      return rejectWithValue(null); // öneri yoksa sessiz geç
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────
const emptyList = { items: [], count: 0, loading: false, error: null };

const initialState = {
  // listKey → {items, count, loading, error}  (listKey: sekme kimliği)
  lists: {},
  counters: {}, // scope → {acik, yaklasan, geciken, tamamlanan_7g}
  detail: { data: null, loading: false, error: null },
  recents: { users: [], units: [] },
};

const slice = createSlice({
  name: "internalTasks",
  initialState,
  reducers: {
    clearTaskDetail(state) {
      state.detail = { data: null, loading: false, error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasksThunk.pending, (state, action) => {
        const { listKey } = action.meta.arg;
        state.lists[listKey] = { ...(state.lists[listKey] || emptyList), loading: true, error: null };
      })
      .addCase(fetchTasksThunk.fulfilled, (state, action) => {
        const { listKey, data } = action.payload;
        state.lists[listKey] = {
          items: data.results || [],
          count: data.count || 0,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchTasksThunk.rejected, (state, action) => {
        const listKey = action.payload?.listKey || action.meta.arg.listKey;
        state.lists[listKey] = {
          ...(state.lists[listKey] || emptyList),
          loading: false,
          error: action.payload?.error || "Liste alınamadı",
        };
      })
      .addCase(fetchCountersThunk.fulfilled, (state, action) => {
        state.counters[action.payload.scope] = action.payload.data;
      })
      .addCase(fetchTaskDetailThunk.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(fetchTaskDetailThunk.fulfilled, (state, action) => {
        state.detail = { data: action.payload, loading: false, error: null };
      })
      .addCase(fetchTaskDetailThunk.rejected, (state, action) => {
        state.detail = { data: null, loading: false, error: action.payload };
      })
      .addCase(fetchRecentTargetsThunk.fulfilled, (state, action) => {
        state.recents = action.payload || { users: [], units: [] };
      });
  },
});

export const { clearTaskDetail } = slice.actions;

// ── Selectors ──────────────────────────────────────────────────────────
export const selectTaskList = (listKey) => (state) =>
  state.internalTasks.lists[listKey] || emptyList;
export const selectTaskCounters = (scope) => (state) =>
  state.internalTasks.counters[scope] || null;
export const selectTaskDetail = (state) => state.internalTasks.detail;
export const selectRecentTargets = (state) => state.internalTasks.recents;

export default slice.reducer;
